import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

import { TextTooltip } from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { CONTENT_TYPES, MATCH_TYPES } from "../ai-curriculum.constants";

import "./ai-recommendation-card.scss";

const formatConfidence = (value) => {
  if (value == null) return "—";
  return value.toFixed(2);
};

const getDisplayName = (item) => {
  if (item.contentType === CONTENT_TYPES.SIMULATION) {
    return item.simulationName;
  }
  if (item.contentType === CONTENT_TYPES.CURRICULUM_RESOURCE) {
    return item.resourceName;
  }
  return "Recommended item";
};

const getContentTypeLabel = (item) =>
  item.contentType === CONTENT_TYPES.SIMULATION ? "Simulation" : "Resource";

// Render a single standard as "{pathwayName} {notation}". Each side is
// nullable in the API, so we drop missing parts; if both are missing,
// returns null and the caller hides the line entirely.
const formatStandardLine = (standard) => {
  if (!standard) return null;
  const parts = [standard.pathwayName, standard.notation].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : null;
};

// Build the "+ N more" tooltip text. Standards are grouped by pathway so a
// shared pathway isn't repeated for each notation.
const formatStandardsTooltip = (standards) => {
  const groups = new Map();
  for (const s of standards) {
    const key = s.pathwayName || "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  return [...groups.entries()]
    .map(([pathway, items]) => {
      const notations = items
        .map((i) => i.notation)
        .filter(Boolean)
        .join(", ");
      return notations ? `${pathway}: ${notations}` : pathway;
    })
    .join(" • ");
};

// Build the hover-details string surfaced by the info icon: the match
// rationale, aligned standards, and match confidence. Nothing is lost from the
// pre-redesign card — it's consolidated behind the icon to match the design.
const buildInfoText = (item, isExact, alignedStandards) => {
  const parts = [];
  if (item.why) parts.push(item.why);
  if (alignedStandards.length > 0) {
    const primary = formatStandardLine(alignedStandards[0]);
    const detail =
      alignedStandards.length > 1
        ? formatStandardsTooltip(alignedStandards)
        : primary;
    if (detail) parts.push(`Aligned to: ${detail}`);
  }
  if (item.confidence != null) {
    parts.push(
      `Match: ${isExact ? "Exact" : "Flexible"} (${formatConfidence(
        item.confidence
      )})`
    );
  }
  return parts.join(" • ");
};

export const AiRecommendationCard = ({ item, onRemove, onRestore }) => {
  const isRemoved = item.removedByUser;
  const isExact = item.matchType === MATCH_TYPES.EXACT;

  const alignedStandards = item.alignedStandards ?? [];
  const infoText = buildInfoText(item, isExact, alignedStandards);

  return (
    <div
      className={clsx(
        "ai-recommendation-card",
        isRemoved && "ai-recommendation-card--removed"
      )}
    >
      <div className="ai-recommendation-card__main">
        <span className="ai-recommendation-card__type">
          {getContentTypeLabel(item)}
        </span>
        <div className="ai-recommendation-card__title-row">
          <span className="ai-recommendation-card__title">
            {getDisplayName(item)}
          </span>
          {infoText && (
            <TextTooltip text={infoText}>
              <FontAwesomeIcon
                className="ai-recommendation-card__info"
                icon={["fa-regular", "circle-info"]}
              />
            </TextTooltip>
          )}
        </div>
      </div>

      <div className="ai-recommendation-card__actions">
        {isRemoved ? (
          <Button
            className="ai-recommendation-card__btn"
            icon={["fa-regular", "arrow-rotate-left"]}
            onClick={() => onRestore(item.id)}
            tooltip="Restore"
          />
        ) : (
          <Button
            className="ai-recommendation-card__btn"
            icon={["fa-regular", "xmark"]}
            onClick={() => onRemove(item.id)}
            tooltip="Remove"
          />
        )}
      </div>

      {isRemoved && (
        <div className="ai-recommendation-card__removed-overlay">Removed</div>
      )}
    </div>
  );
};
